import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Grid, IconButton, InputAdornment, TextField } from '@mui/material'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import SearchIcon from '@mui/icons-material/Search'
import { updateQuery } from '../state/actions'
import {
  CustomersBySearchQueryProps,
  SearchQueryConstants,
  UseSearchByQueryOfferPackage,
  getTopBarSearchQuery
} from '@cbgms/api/modules/customers'
import { useSafeState } from '@cbgms/base/hooks/use-safe-state'
import { MenuItem } from 'core/page/MenuItem'
import { useNotifier } from '@cbgms/base/notifier'
import store from '../state/store'

export const SearchByQuery: React.FC<CustomersBySearchQueryProps> = ({ setAPISearchQuery, handleReload }) => {
  const [queryInputValue, setqueryInputValue] = useSafeState<string>('')

  const { t } = useTranslation()
  const notifier = useNotifier()

  useEffect(() => {
    const unsubscribe = store.subscribe(() => {
      const state = store.getState()
      setqueryInputValue(state.Query)
    })
    return () => {
      unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (queryInputValue === '') {
      updateSearchQuery('')
    }
  }, [queryInputValue])

  const getCustomersBySearchQuery = React.useCallback(async (): Promise<string> => {
    const { Query } = store.getState()
    let queryString = ''
    if (Query) {
      queryString += getTopBarSearchQuery(Query)
    }
    return queryString
  }, [])

  const updateSearchQuery = React.useCallback(
    async (QueryString: string) => {
      try {
        if (QueryString.length >= SearchQueryConstants.MIN_QUERY_LENGTH) {
          store.dispatch(updateQuery(QueryString))
          const customerSearchQueryInList = await getCustomersBySearchQuery()
          setAPISearchQuery(customerSearchQueryInList)
        }
        if (QueryString.length <= 0) {
          store.dispatch(updateQuery(QueryString))
          setAPISearchQuery('')
        }
      } catch (e: any) {
        notifier.error(e.message)
      }
    },
    [setAPISearchQuery]
  )

  const queryStringOnChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const searchQuery = event.target.value.slice(0, 255)
    setqueryInputValue(searchQuery)
    updateQuery(searchQuery)
  }, [])

  const queryStringOnKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      updateSearchQuery(queryInputValue)
    }
  }

  return (
    <Grid item display='flex' alignItems='center' gap={1} data-testid='search-component'>
      <TextField
        size='small'
        sx={{ minWidth: 560 }}
        placeholder={t('grid.search_by_eos_id_cust_name_ship_to_location')}
        type='text'
        onChange={queryStringOnChange}
        onKeyDown={queryStringOnKeyDown}
        value={queryInputValue}
        inputProps={{ 'data-testid': 'search-component-input' }}
        InputProps={{
          endAdornment: (
            <InputAdornment position='end'>
              {queryInputValue && (
                <IconButton
                  onClick={() => {
                    handleReload && handleReload()
                    setqueryInputValue('')
                    updateSearchQuery('')
                  }}
                  size='large'
                >
                  <HighlightOffIcon />
                </IconButton>
              )}
            </InputAdornment>
          )
        }}
      />
      <Button
        startIcon={<SearchIcon />}
        variant='contained'
        disableElevation
        onClick={() => updateSearchQuery(queryInputValue)}
        disabled={queryInputValue.length < 3}
        data-testid='search-component-button'
      >
        {t('global.search')}
      </Button>
    </Grid>
  )
}

export const useSearchCustomers = ({ handleReload }: { handleReload: () => void }): UseSearchByQueryOfferPackage => {
  const [apiSearchQuery, setAPISearchQuery] = useSafeState<string>('')

  return {
    getSearchComponent: () => (
      <MenuItem>
        <SearchByQuery handleReload={handleReload} setAPISearchQuery={setAPISearchQuery} />
      </MenuItem>
    ),
    apiSearchQuery
  }
}
